import { ProgramTaskRoadmap } from "./ProgramTaskRoadmap";
import { RetailRoadmapInternal } from "@/components/roadmap/RetailRoadmapInternal";
import { RequirementsList } from "@/components/people/overview/RequirementsList";
import { enrichRequirements, MILESTONE_STATUS, TONE } from "@/components/people/overview/programUtils";
import { ManageProgramProgress } from "@/components/programs/ManageProgramProgress";
import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { viewAttachment } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RoadmapStrip, StatusChip } from "@/components/RoadmapStrip";
import { toast } from "sonner";
import { Workflow, Paperclip, FileText, ClipboardList, X, Zap, ShieldCheck, Upload, Download, Lock, CheckCircle2, AlertTriangle, Landmark } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const PROGRAMS = [
  { key: "retail", label: "Retail" },
  { key: "associate", label: "Associate Agent" },
  { key: "protege", label: "Protégé" },
  { key: "agency_producer", label: "Agency Producer" },
];
const PROGRAM_LABEL = { retail: "Retail", associate: "Associate Agent", protege: "Protégé", agency_producer: "Agency Producer" };

function MilestoneSummary({ data, tasks, onOpenLicensing, onChanged }) {
  const items = enrichRequirements(data.requirements, tasks);
  const notConfigured = !data.required_total || (data.config_status && data.config_status !== "ok");
  const counts = {};
  items.forEach((r) => { const l = (MILESTONE_STATUS[r.status] || [r.status])[0].split(" · ")[0]; counts[l] = (counts[l] || 0) + 1; });
  const lic = items.filter((r) => /licens/i.test(`${r.category} ${r.title}`));
  return (
    <div className="space-y-4" data-testid="ms-overview">
      {notConfigured ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="ms-not-configured">
          <p className="font-semibold text-amber-800">Program requirements not configured</p>
          <p className="text-sm text-amber-800 mt-1">This person is enrolled, but the program's requirements aren't fully set up or mapped. A count of zero does <b>not</b> mean the program is complete. An admin can fix this under Admin Center → Programs, then use Sync from records.</p>
          <a href="/admin/programs" className="mt-2 inline-block text-sm font-medium text-brand hover:underline" data-testid="ms-config-link">Open program configuration →</a>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ms-phase-progress">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <p className="text-sm font-semibold text-slate-900 mr-2">What must be completed to advance</p>
            {Object.entries(counts).map(([k, v]) => <span key={k} className="text-[11px] rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{k}: {v}</span>)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(data.phases || []).filter((p) => p.required_total).map((p) => (
              <div key={p.id} className={`rounded-lg border px-3 py-2 ${p.is_current ? "border-brand/40 bg-brand/5" : "border-slate-200"}`} data-testid={`ms-phase-${p.id}`}>
                <div className="flex justify-between text-xs"><span className="font-medium text-slate-700">{p.name}{p.is_current ? " · current" : ""}</span><span className="tabular-nums text-slate-500">{p.required_complete}/{p.required_total}</span></div>
                <div className="h-1.5 mt-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-brand rounded-full" style={{ width: `${p.pct || 0}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
      {items.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ms-outstanding">
          <p className="text-sm font-semibold text-slate-900 mb-3">Outstanding requirements, responsible people and due dates</p>
          <RequirementsList items={items} quick onChanged={onChanged} />
        </div>
      )}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ms-licensing">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand" />Licensing &amp; Compliance</p>
          {onOpenLicensing && <button onClick={onOpenLicensing} className="text-xs font-medium text-brand hover:underline" data-testid="ms-open-licenses">This person's license records →</button>}
        </div>
        {lic.length ? (
          <ul className="space-y-1">
            {lic.map((r) => { const [l, t] = MILESTONE_STATUS[r.status] || [r.status, "slate"]; return (
              <li key={r.requirement_id} className="flex justify-between gap-2 text-sm"><span className="text-slate-700">{r.title}</span><span className={`text-[11px] rounded-full px-2 py-0.5 ${TONE[t]}`}>{l}</span></li>
            ); })}
          </ul>
        ) : <p className="text-sm text-slate-400">No licensing requirements in this program.</p>}
        <p className="text-[11px] text-slate-400 mt-2">Requirements about staff or producer licenses are verified on those people's own records and are not this person's license.</p>
      </div>
    </div>
  );
}

export function MilestonesTab({ personId, canManage: canManageProp, tasks = [], onOpenLicensing, enrollmentId = null, onTasksChanged }) {
  const canManage = canManageProp && !enrollmentId;
  const [data, setData] = useState(null);
  const [program, setProgram] = useState("retail");
  const [subtype, setSubtype] = useState("traditional");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!canManage) return;
    api.get("/programs/enrollment-preview", { params: { person_id: personId, program, program_subtype: program === "retail" ? subtype : undefined } })
      .then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [personId, program, subtype, canManage]);

  const load = useCallback(() => {
    api.get(`/programs/enrollments/${personId}`, { params: enrollmentId ? { enrollment_id: enrollmentId } : {} }).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); setData({ enrollment: null }); });
  }, [personId, enrollmentId]);
  useEffect(() => { load(); }, [load]);

  const enroll = async () => {
    setBusy(true);
    try {
      await api.post("/programs/enrollments", { person_id: personId, program, program_subtype: program === "retail" ? subtype : null });
      toast.success("Enrolled in program");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const reconcile = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/programs/enrollments/${data.enrollment.id}/reconcile`);
      const s = r.data.summary || {};
      const done = (s.completed || []).length, rev = (s.ready_for_review || []).length;
      toast.success(done || rev ? `Synced from records — ${done} completed, ${rev} ready for review` : "No new matches from existing records");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const linkEvidence = async (statusId, payload) => {
    try {
      await api.post(`/programs/milestone-status/${statusId}/evidence`, payload);
      toast.success("Evidence linked — moved to Ready for Review");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const unlinkEvidence = async (statusId, evidenceId) => {
    try {
      await api.delete(`/programs/milestone-status/${statusId}/evidence/${evidenceId}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (data === null) return <p className="text-sm text-slate-400">Loading…</p>;

  if (!data.enrollment) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center">
        <Workflow className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-600 mb-4">No program enrollment yet.</p>
        {canManage ? (
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Select value={program} onValueChange={setProgram}>
              <SelectTrigger className="w-44" data-testid="enroll-program"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{PROGRAMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            {program === "retail" && (
              <Select value={subtype} onValueChange={setSubtype}>
                <SelectTrigger className="w-40" data-testid="enroll-subtype"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="traditional">Traditional</SelectItem><SelectItem value="acquisition">Acquisition</SelectItem></SelectContent>
              </Select>
            )}
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogTrigger asChild>
                <Button disabled={busy} data-testid="enroll-submit" className="bg-[#006090] hover:bg-[#004E75]">Enroll</Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white" data-testid="enroll-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Enroll this candidate in {PROGRAM_LABEL[program]}{program === "retail" ? ` · ${subtype}` : ""}?</AlertDialogTitle>
                  <AlertDialogDescription>Review what will happen before enrolling. Existing Top-Recruit records are reconciled first — no duplicate records are created.</AlertDialogDescription>
                </AlertDialogHeader>
                {preview?.configured ? (
                  <div className="rounded-lg border border-slate-200 divide-y text-sm" data-testid="enroll-confirm-stats">
                    {[["Program version", `v${preview.version}`], ["Requirements", preview.total], ["Already satisfied from records", preview.already_satisfied], ["Outstanding", preview.outstanding], ["Tasks that will be created", preview.tasks_to_create], ["Candidate-visible tasks", preview.candidate_visible], ["Internal-only tasks", preview.internal_only]].map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between px-3 py-1.5"><span className="text-slate-500">{k}</span><span className="font-semibold text-slate-900">{v}</span></div>
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-500">This program has no checklist configured for this account yet.</p>}
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="enroll-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={enroll} disabled={busy} data-testid="enroll-confirm" className="bg-[#006090] hover:bg-[#004E75]">Enroll in Program</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : <p className="text-xs text-slate-400">A recruiter will enroll this candidate.</p>}
        {canManage && preview?.configured && (
          <div className="mt-4 text-xs text-slate-500 border-t border-slate-100 pt-3" data-testid="enroll-preview">
            Enrolling will initialize <strong>{preview.total}</strong> requirements — <strong className="text-emerald-600">{preview.already_satisfied}</strong> already satisfied from existing records, <strong>{preview.tasks_to_create}</strong> tasks will be created for outstanding items.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <MilestoneSummary data={data} tasks={tasks} onOpenLicensing={onOpenLicensing} onChanged={() => { load(); onTasksChanged?.(); }} />
      {canManage && (
        <div className="flex items-center justify-end">
          <Button variant="outline" size="sm" onClick={reconcile} disabled={busy} data-testid="ms-reconcile-btn" className="text-xs">
            <Zap className="w-3.5 h-3.5 mr-1.5" /> Sync from records
          </Button>
        </div>
      )}
      {canManage && <ManageProgramProgress data={data} onChanged={load} />}
      {data.reserve_gate && (
        <ReserveGateBanner gate={data.reserve_gate} />
      )}
      {data.reserve_gate && (
        <CapitalPanel enrollmentId={data.enrollment.id} canManage={canManage} onChanged={load} />
      )}
      <ProgramTaskRoadmap enrollmentId={data.enrollment.id} canManage={canManage} />
      <RetailRoadmapInternal personId={data.enrollment.person_id} />
      {data.career_gate && (
        <ReserveGateBanner gate={data.career_gate} kind="career" />
      )}
      {data.required_total > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ms-summary">
          <p className="text-sm text-slate-700">
            <strong className="text-slate-900">{data.required_complete} of {data.required_total}</strong> requirements complete
            {data.auto_completed > 0 && <> · <span className="text-emerald-700 font-medium">{data.auto_completed} completed automatically from existing Top-Recruit records</span></>}
          </p>
          {data.matched_records?.length > 0 && (
            <details className="mt-2" data-testid="ms-matched-details">
              <summary className="text-xs text-[#006090] cursor-pointer select-none">View matched records</summary>
              <ul className="mt-2 space-y-1">
                {data.matched_records.map((m, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`ms-matched-${i}`}>
                    <Zap className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span className="font-medium text-slate-700">{m.requirement}</span>
                    <span className="text-slate-400">— {m.label}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {data.enrollment.program === "protege" && (
        <>
          <MentorSection enrollment={data.enrollment} mentorName={data.mentor_name} mentorAgency={data.mentor_agency_name} canManage={canManage} onSaved={load} />
          <SponsorSection enrollment={data.enrollment} sponsorName={data.sponsoring_agent_name} canManage={canManage} onSaved={load} />
        </>
      )}
      <RoadmapStrip
        data={data}
        programLabel={`${PROGRAM_LABEL[data.enrollment.program] || data.enrollment.program}${data.enrollment.program_subtype ? ` · ${data.enrollment.program_subtype}` : ""} — v${data.enrollment.version_label}`}
        renderRequirement={(r) => <StatusChip status={r.status} />}
        renderEvidence={canManage ? (r) => (
          <EvidenceRow r={r} personId={personId}
            onLink={(payload) => linkEvidence(r.status_id, payload)}
            onUnlink={(eid) => unlinkEvidence(r.status_id, eid)} />
        ) : undefined}
      />
    </div>
  );
}

function EvidenceRow({ r, personId, onLink, onUnlink }) {
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState(null);
  const evidence = r.evidence || [];

  const loadSources = () => {
    if (sources) return;
    api.get(`/programs/person-evidence-sources/${personId}`).then((res) => setSources(res.data)).catch((e) => toast.error(apiError(e)));
  };

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`ms-evidence-${r.requirement_id}`}>
      {evidence.map((e) => (
        <span key={e.id} className={`inline-flex items-center gap-1 text-[11px] border rounded px-1.5 py-0.5 ${e.auto ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
          {e.auto ? <Zap className="w-3 h-3" /> : e.type === "task" ? <ClipboardList className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
          {e.label || "Evidence"}
          {!e.auto && <button onClick={() => onUnlink(e.id)} className="text-slate-400 hover:text-rose-500" data-testid={`ms-evidence-remove-${e.id}`}><X className="w-3 h-3" /></button>}
        </span>
      ))}
      {r.status_id && (
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) loadSources(); }}>
          <PopoverTrigger asChild>
            <button className="inline-flex items-center gap-1 text-[11px] text-[#006090] border border-[#006090]/30 rounded px-1.5 py-0.5 hover:bg-[#006090]/5" data-testid={`ms-evidence-link-${r.requirement_id}`}>
              <Paperclip className="w-3 h-3" /> Link evidence
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 bg-white p-0 max-h-80 overflow-y-auto" align="end">
            {!sources ? <p className="p-3 text-xs text-slate-400">Loading…</p> : (
              <div className="p-2 space-y-3">
                <div>
                  <p className="px-2 py-1 text-[11px] font-semibold text-slate-400 uppercase">Uploaded documents</p>
                  {sources.attachments.length === 0 && <p className="px-2 text-xs text-slate-400">None yet</p>}
                  {sources.attachments.map((a) => (
                    <button key={a.id} onClick={() => { onLink({ type: "attachment", ref_id: a.id, label: a.label }); setOpen(false); }}
                      className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-50 flex items-center gap-2" data-testid={`ms-src-att-${a.id}`}>
                      <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{a.label}{a.from_candidate ? " · from candidate" : ""}</span>
                    </button>
                  ))}
                </div>
                <div>
                  <p className="px-2 py-1 text-[11px] font-semibold text-slate-400 uppercase">Candidate tasks</p>
                  {sources.tasks.length === 0 && <p className="px-2 text-xs text-slate-400">None</p>}
                  {sources.tasks.map((t) => (
                    <button key={t.id} onClick={() => { onLink({ type: "task", ref_id: t.id, label: t.label }); setOpen(false); }}
                      className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-50 flex items-center gap-2" data-testid={`ms-src-task-${t.id}`}>
                      <ClipboardList className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{t.label}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => { onLink({ type: "license", label: "License record confirmed" }); setOpen(false); }}
                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-50 text-slate-600" data-testid="ms-src-license">
                  + Mark license record as verified
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}


function MentorSection({ enrollment, mentorName, mentorAgency, canManage, onSaved }) {
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    if (canManage) api.get("/persons", { params: { classification: "agent", page_size: 200 } })
      .then((r) => setAgents(r.data.persons || r.data.items || [])).catch(() => {});
  }, [canManage]);
  const save = async (pid) => {
    try { await api.patch(`/programs/enrollments/${enrollment.id}/mentor`, { mentor_person_id: pid === "none" ? null : pid }); toast.success("Mentor updated"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border border-[#0D9488]/20 bg-[#0D9488]/5 p-4" data-testid="mentor-section">
      <p className="text-xs font-semibold text-slate-500 uppercase">Mentor Agent</p>
      <p className="mt-0.5 font-semibold text-slate-900" data-testid="mentor-name">{mentorName || "Not assigned"}{mentorAgency ? ` · ${mentorAgency}` : ""}</p>
      {canManage && (
        <Select value={enrollment.mentor_person_id || "none"} onValueChange={save}>
          <SelectTrigger className="mt-2 w-72" data-testid="mentor-select"><SelectValue placeholder="Assign mentor agent" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            <SelectItem value="none">Not assigned</SelectItem>
            {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}{a.organization_name ? ` · ${a.organization_name}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}


function SponsorSection({ enrollment, sponsorName, canManage, onSaved }) {
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    if (canManage) api.get("/sponsorship/agents").then((r) => setAgents(r.data.items || [])).catch(() => {});
  }, [canManage]);
  const save = async (uid) => {
    try {
      const r = await api.patch(`/sponsorship/enrollments/${enrollment.id}`, { user_id: uid === "none" ? null : uid });
      toast.success(`Sponsoring Agent updated${r.data.tasks_reassigned ? ` · ${r.data.tasks_reassigned} task(s) reassigned` : ""}`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border border-[#0F2B5B]/15 bg-[#0F2B5B]/5 p-4" data-testid="sponsor-section">
      <p className="text-xs font-semibold text-slate-500 uppercase">Sponsoring (Lead) Agent</p>
      <p className="mt-0.5 font-semibold text-slate-900" data-testid="sponsor-name">{sponsorName || "Not linked — sponsoring tasks stay Not Assigned"}</p>
      {canManage && (
        <Select value={enrollment.sponsoring_agent_user_id || "none"} onValueChange={save}>
          <SelectTrigger className="mt-2 w-72" data-testid="sponsor-select"><SelectValue placeholder="Link sponsoring agent" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            <SelectItem value="none">Not linked</SelectItem>
            {agents.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`sponsor-option-${a.id}`}>{a.name || a.email}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}


const CAP_META = {
  not_submitted: { label: "Not submitted", cls: "bg-slate-50 border-slate-200 text-slate-500" },
  submitted: { label: "Submitted — pending review", cls: "bg-sky-50 border-sky-200 text-[#006090]" },
  verified: { label: "Verified", cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  rejected: { label: "Needs re-submission", cls: "bg-rose-50 border-rose-200 text-rose-600" },
  expired: { label: "Expired — re-verify", cls: "bg-amber-50 border-amber-200 text-amber-700" },
};
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);

function ReserveGateBanner({ gate, kind = "reserve" }) {
  const ready = gate.ready;
  const L = kind === "career" ? {
    testid: "career-gate", statusTestid: "career-gate-status", blockTestid: "career-gate-blocking",
    readyTitle: "Ready for Career Conversion Request", lockedTitle: "Career Conversion Request — Locked",
    readyText: "All Career Conversion prerequisites are verified. You can submit the New Agency Appointment Request.",
    lockedText: "Complete and verify all Career Conversion prerequisites before the appointment request can be submitted.",
  } : {
    testid: "reserve-gate", statusTestid: "reserve-gate-status", blockTestid: "reserve-gate-blocking",
    readyTitle: "Ready for Reserve Appointment Request", lockedTitle: "Reserve Appointment Request — Locked",
    readyText: "All pre-reserve prerequisites are verified. You can submit the Reserve Agent Appointment Request.",
    lockedText: "Complete and verify all pre-reserve prerequisites before the appointment request can be submitted.",
  };
  return (
    <div className={`rounded-xl border p-4 ${ready ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid={L.testid}>
      <div className="flex items-start gap-3">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${ready ? "bg-emerald-500 text-white" : "bg-amber-400 text-white"}`}>
          {ready ? <CheckCircle2 className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
        </span>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900" data-testid={L.statusTestid}>
            {ready ? L.readyTitle : L.lockedTitle}
          </p>
          <p className="text-xs text-slate-600 mt-0.5">
            {ready ? L.readyText : L.lockedText}
          </p>
          {!ready && gate.blocking?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={L.blockTestid}>
              {gate.blocking.map((b) => (
                <span key={b} className="inline-flex items-center gap-1 text-[11px] bg-white border border-amber-200 text-amber-700 rounded px-1.5 py-0.5">
                  <AlertTriangle className="w-3 h-3" /> {b}
                </span>
              ))}
            </div>
          )}
          {gate.submit_title && (
            <p className="mt-2 text-[11px] text-slate-500">
              {gate.submit_title}: <span className="font-medium capitalize">{(gate.submit_status || "not_started").replace(/_/g, " ")}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function CapitalPanel({ enrollmentId, canManage, onChanged }) {
  const [cap, setCap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/programs/enrollments/${enrollmentId}/capital`).then((r) => { setCap(r.data); setAmount(r.data.amount_verified || r.data.amount_required || ""); }).catch(() => setCap(false));
  }, [enrollmentId]);
  useEffect(() => { load(); }, [load]);

  if (cap === null) return <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-400" data-testid="capital-panel">Loading capital verification…</div>;
  if (cap === false) return null;

  const meta = CAP_META[cap.status] || CAP_META.not_submitted;

  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25 MB)."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/programs/enrollments/${enrollmentId}/capital/upload`, fd);
      toast.success("Financial document uploaded (internal-only)"); load(); onChanged?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await api.post(`/programs/enrollments/${enrollmentId}/capital/verify`, { amount: amount ? Number(amount) : undefined, note: note || undefined });
      toast.success("Capital verified"); setNote(""); load(); onChanged?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!reason.trim()) { toast.error("Add a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/programs/enrollments/${enrollmentId}/capital/reject`, { reason });
      toast.success("Marked for re-submission"); setReason(""); load(); onChanged?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const download = async (d) => {
    try { await viewAttachment(`/attachments/${d.attachment_id}/file`, d.filename); }
    catch (err) { toast.error(apiError(err)); }
  };

  const expiringSoon = cap.status === "verified" && cap.days_left != null && cap.days_left <= 14;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="capital-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="w-9 h-9 rounded-lg bg-[#006090]/10 text-[#006090] flex items-center justify-center shrink-0"><Landmark className="w-5 h-5" /></span>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 flex items-center gap-2">Capital Verification
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 border border-slate-200 rounded px-1 py-0.5"><Lock className="w-2.5 h-2.5" /> Internal only</span>
            </p>
            <p className="text-xs text-slate-500">Requires {money(cap.amount_required)} in liquid assets · recency {cap.recency_days} days · no waiver</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls}`} data-testid="capital-status">
          <ShieldCheck className="w-3 h-3" /> {meta.label}
        </span>
      </div>

      {cap.status === "verified" && (
        <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${expiringSoon ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`} data-testid="capital-verified-meta">
          Verified {money(cap.amount_verified)} by {cap.verified_by_name || "internal"} · on {(cap.verified_at || "").slice(0, 10)} · expires {(cap.expires_at || "").slice(0, 10)}
          {cap.days_left != null && <> ({cap.days_left >= 0 ? `${cap.days_left} days left` : "expired"})</>}
        </div>
      )}
      {cap.status === "rejected" && cap.rejected_reason && (
        <p className="mt-3 text-xs text-rose-600" data-testid="capital-reject-reason">Reason: {cap.rejected_reason}</p>
      )}

      {cap.documents?.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-semibold text-slate-400 uppercase mb-1">Evidence documents</p>
          <div className="flex flex-wrap gap-2">
            {cap.documents.map((d, i) => (
              <button key={d.attachment_id || i} onClick={() => download(d)} data-testid={`capital-doc-${i}`}
                className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 hover:border-[#006090] hover:text-[#006090]">
                <FileText className="w-3.5 h-3.5" /> {d.filename || "Document"}{d.from_candidate ? " · candidate" : ""} <Download className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
      )}

      {canManage && (
        <div className="mt-4 flex items-center gap-2 flex-wrap border-t border-slate-100 pt-3">
          <input ref={fileRef} type="file" className="hidden" onChange={upload} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,.xlsx,.csv" data-testid="capital-upload-input" />
          <Button variant="outline" size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="capital-upload-btn" className="text-xs">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload document
          </Button>

          {(cap.status === "submitted" || cap.status === "rejected" || cap.status === "expired") && (
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" disabled={busy || cap.document_count === 0} data-testid="capital-verify-open" className="text-xs bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> {cap.status === "expired" ? "Re-verify" : "Verify"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 bg-white p-3 space-y-2" align="start">
                <p className="text-xs font-semibold text-slate-700">Verify capital evidence</p>
                <label className="text-[11px] text-slate-500">Amount verified</label>
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100000" data-testid="capital-amount-input" className="h-8 text-sm" />
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" data-testid="capital-note-input" className="text-sm" rows={2} />
                <Button size="sm" onClick={verify} disabled={busy} data-testid="capital-verify-confirm" className="w-full text-xs bg-emerald-600 hover:bg-emerald-700">Confirm Verified</Button>
              </PopoverContent>
            </Popover>
          )}

          {(cap.status === "submitted" || cap.status === "verified") && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" disabled={busy} data-testid="capital-reject-open" className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50">
                  Mark for re-submission
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 bg-white p-3 space-y-2" align="start">
                <p className="text-xs font-semibold text-slate-700">Reason for re-submission</p>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Statement older than 90 days" data-testid="capital-reject-input" className="text-sm" rows={2} />
                <Button size="sm" onClick={reject} disabled={busy} data-testid="capital-reject-confirm" className="w-full text-xs bg-rose-600 hover:bg-rose-700">Request re-submission</Button>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
    </div>
  );
}